import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, IconButton } from '@chakra-ui/react';
import { Tooltip } from '@src/components/chakra-ui/tooltip';
import { chatCommentsForGenerateDialog, chatGenerateAccountUserId, chatGenerateOffered, chatGenerateReplyInput } from '@src/components/sco/Chat/chatGenerate';
import { useChatGenerateTools } from '@src/components/sco/Chat/useChatGenerateTools';
import { COMMENT_STORED_FILES_SELECTION } from '@src/components/sco/Comments/CommentAttachments';
import { GenerateReplyDialog } from '@src/components/sco/GenerateReply/GenerateReplyDialog';
import { commentsForGenerate, type GenerateDraftRequest } from '@src/components/sco/GenerateReply/generateReply';
import { GENERATE_CRM_REPLY } from '@src/gql/mutations/GenerateCrmReply';
import { gql } from '@src/lib/gql';
import { prettyErrorMessage } from '@src/lib/mutationErrors';
import React, { useRef, useState } from 'react';
import { LuSparkles } from 'react-icons/lu';
export const GENERATE_THREAD = gql`
  query ChatGenerateThread($threadId: String) {
    chat {
      id
      thread(threadId: $threadId) {
        id
        threadId
        threadTypeKey
        userMemberships {
          id
          items {
            id
            userId
            user {
              id
              userId
              responderToolId
            }
          }
        }
      }
    }
  }
`;
export const GENERATE_COMMENTS = gql`
  query ChatGenerateComments(
    $collectionId: String
    $order: [[String]]
    $pageSize: Int
    $threadId: String
  ) {
    forum {
      id
      comments(collectionId: $collectionId, order: $order, pageSize: $pageSize, threadId: $threadId) {
        id
        items {
          id
          commentId
          body
          createdAt
          createdByUserId
          deletedAt
          isDraft
          withdrawnAt
          ${COMMENT_STORED_FILES_SELECTION}
        }
      }
    }
  }
`;

/**
 * Runs the generate mutation and hands a non-empty reply to `write`. Returns the error message to
 * show, or null on success. Never throws. Kept outside the component so the React Compiler does not
 * have to lower a try statement.
 */
async function generateAndWrite(send: () => Promise<{
  data?: {
    generateCrmReply?: {
      body?: string | null;
    } | null;
  } | null;
}>, write: (written: string) => void): Promise<string | null> {
  try {
    const result = await send();
    const written = String(result.data?.generateCrmReply?.body || '').trim();
    if (!written) {
      return 'The model returned an empty reply.';
    }
    write(written);
    return null;
  } catch (err) {
    return prettyErrorMessage(err);
  }
}

/**
 * Generate on the member chat composer. Sco admins only, on a direct chat that has a reply
 * account. The reply is written into the box. Nothing is sent, and the rule run is left alone.
 */
export function ChatGenerateControl(props) {
  const $ = _c(34);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const [generateReply] = useMutation(GENERATE_CRM_REPLY);
  let t0;
  if ($[0] !== props.threadId) {
    t0 = {
      variables: {
        threadId: props.threadId
      }
    };
    $[0] = props.threadId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const threadQuery = useQuery(GENERATE_THREAD, t0);
  const thread = threadQuery.data?.chat?.thread;
  const accountUserId = chatGenerateAccountUserId(props.userId, (thread?.userMemberships?.items || []).map(_temp));
  const offered = chatGenerateOffered({
    accountUserId,
    isScoAdmin: true,
    threadTypeKey: thread?.threadTypeKey
  });
  const responderToolId = (thread?.userMemberships?.items || []).find(item_0 => (item_0?.user?.userId || item_0?.userId) === accountUserId)?.user?.responderToolId || null;
  const {
    tools
  } = useChatGenerateTools(offered, responderToolId);
  const t1 = !offered;
  const t2 = `ChatGenerate:${props.threadId}:comments`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [["createdAt", "DESC"]];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== props.threadId || $[4] !== t2) {
    t4 = {
      collectionId: t2,
      order: t3,
      pageSize: 50,
      threadId: props.threadId
    };
    $[3] = props.threadId;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t4) {
    t5 = {
      skip: t1,
      variables: t4
    };
    $[6] = t1;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const commentsQuery = useQuery(GENERATE_COMMENTS, t5);
  const comments = chatCommentsForGenerateDialog(commentsQuery.data?.forum?.comments?.items, accountUserId || "");
  const sentCount = commentsForGenerate(comments, "thread").length;
  const noSentMessages = Boolean(commentsQuery.data) && sentCount === 0;
  let t6;
  if ($[9] !== accountUserId || $[10] !== generateReply || $[11] !== props) {
    t6 = async function generate(request) {
      if (inFlight.current) {
        return null;
      }
      if (!accountUserId) {
        return "Replies are off until this account has a reply tool.";
      }
      const requestedThreadId = props.threadId;
      inFlight.current = true;
      setPending(true);
      props.onPending(true);
      const message = await generateAndWrite(() => generateReply({
        variables: {
          input: chatGenerateReplyInput({
            accountUserId,
            request,
            threadId: requestedThreadId
          })
        }
      }), written => {
        props.onWrite(written, requestedThreadId);
        setOpen(false);
      });
      inFlight.current = false;
      setPending(false);
      props.onPending(false);
      return message;
    };
    $[9] = accountUserId;
    $[10] = generateReply;
    $[11] = props;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const generate = t6;
  if (!offered) {
    return null;
  }
  const generateDisabled = props.busy || pending || commentsQuery.loading || noSentMessages;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      placement: "top"
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== generateDisabled) {
    t8 = generateDisabled ? {
      pointerEvents: "none"
    } : undefined;
    $[14] = generateDisabled;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== commentsQuery) {
    t9 = () => {
      commentsQuery.refetch();
      setOpen(true);
    };
    $[16] = commentsQuery;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <LuSparkles size={18} />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== generateDisabled || $[20] !== t8 || $[21] !== t9) {
    t11 = <Tooltip borderRadius="6px" content="Generate" fontSize="11px" fontWeight="bold" letterSpacing="0.05em" positioning={t7} textTransform="uppercase"><Box display="inline-flex"><IconButton aria-label="Generate" borderRadius="md" css={t8} disabled={generateDisabled} size="sm" type="button" variant="ghost" onClick={t9}>{t10}</IconButton></Box></Tooltip>;
    $[19] = generateDisabled;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = props.busy || pending;
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setOpen(false);
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== comments || $[25] !== generate || $[26] !== open || $[27] !== props.hasTypedText || $[28] !== t12 || $[29] !== tools) {
    t14 = <GenerateReplyDialog busy={t12} comments={comments} needsConfirm={props.hasTypedText} open={open} tools={tools} onClose={t13} onGenerate={generate} />;
    $[24] = comments;
    $[25] = generate;
    $[26] = open;
    $[27] = props.hasTypedText;
    $[28] = t12;
    $[29] = tools;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t11 || $[32] !== t14) {
    t15 = <>{t11}{t14}</>;
    $[31] = t11;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}
function _temp(item) {
  return {
    responderToolId: item?.user?.responderToolId,
    userId: item?.user?.userId || item?.userId
  };
}